import { LoadingOrError } from '@retainerclub/common';
import { useCart, useLocale } from '@retainerclub/hooks';
import PropTypes from 'prop-types';
import { Link } from 'react-router';
import CheckoutCartActions from './CheckoutCartActions';
import CheckoutCartItems from './CheckoutCartItems';
import CheckoutRetainedAttemptStatus from './CheckoutRetainedAttemptStatus';

function CheckoutCart({
  allowEmptyCartRecovery = false,
  commercialInputsLocked = false,
  error = null,
  mutationErrors = null,
  loading = false,
  ctError = null,
  ctLoading = false,
  taxQuote = null,
  retainedAttempt = null,
  recoveryError = null,
  recoveryLoading = false,
  onRetryRecovery,
  requiresPaymentReconciliation = false,
  deliveryUpdateState,
}) {
  const { cart, hasCartItems, cLoading, cError, destroyCartItem } = useCart();
  const { locale } = useLocale();

  const shouldRender = LoadingOrError({
    loading: cLoading,
    error: cError,
    data: { cart },
    dataKey: 'cart',
    title: 'Cart',
    shouldShowError: cError,
  });

  return (
    <div className="mt-10 lg:mt-0">
      <h2 className="font-medium text-gray-900 text-lg">Order summary</h2>

      <div className="mt-4 rounded-lg border border-gray-200 bg-gray-100 shadow-xs">
        <h3 className="sr-only">Items in your cart</h3>
        {shouldRender === true && !hasCartItems ? (
          <div className="px-4 py-10 text-center sm:px-6">
            <p className="font-medium text-base text-gray-900">
              {allowEmptyCartRecovery
                ? 'Your paid checkout is ready to finish'
                : 'Your cart is empty'}
            </p>
            <p className="mt-1 text-gray-500 text-sm">
              {allowEmptyCartRecovery
                ? 'The retained quoted payment and shipping details will be used.'
                : 'Add an item before checking out.'}
            </p>
            {allowEmptyCartRecovery ? null : (
              <Link
                to="/shop"
                className="mt-4 inline-block font-medium text-rcprimary-400 text-sm hover:text-rcprimary-300"
              >
                Continue shopping
              </Link>
            )}
          </div>
        ) : null}
        {shouldRender === true && hasCartItems ? (
          <CheckoutCartItems
            cart={cart}
            commercialInputsLocked={commercialInputsLocked}
            destroyCartItem={destroyCartItem}
            locale={locale}
            onRetryRecovery={onRetryRecovery}
            recoveryError={recoveryError}
            recoveryLoading={recoveryLoading}
            retainedAttempt={retainedAttempt}
            taxQuote={taxQuote}
          />
        ) : null}
        {shouldRender !== true ? shouldRender : null}

        {shouldRender === true && !hasCartItems ? (
          <CheckoutRetainedAttemptStatus
            onRetry={onRetryRecovery}
            recoveryError={recoveryError}
            recoveryLoading={recoveryLoading}
            retainedAttempt={retainedAttempt}
          />
        ) : null}

        {shouldRender === true && (hasCartItems || allowEmptyCartRecovery) ? (
          <CheckoutCartActions
            commercialInputsLocked={commercialInputsLocked}
            ctError={ctError}
            ctLoading={ctLoading}
            deliveryUpdateState={deliveryUpdateState}
            error={error}
            loading={loading}
            mutationErrors={mutationErrors}
            recoveryError={recoveryError}
            recoveryLoading={recoveryLoading}
            requiresPaymentReconciliation={requiresPaymentReconciliation}
            retainedAttempt={retainedAttempt}
            taxQuote={taxQuote}
          />
        ) : null}
      </div>
    </div>
  );
}

CheckoutCart.propTypes = {
  allowEmptyCartRecovery: PropTypes.bool,
  commercialInputsLocked: PropTypes.bool,
  error: PropTypes.object,
  mutationErrors: PropTypes.array,
  loading: PropTypes.bool,
  ctError: PropTypes.object,
  ctLoading: PropTypes.bool,
  taxQuote: PropTypes.shape({
    capability: PropTypes.shape({
      enabled: PropTypes.bool.isRequired,
      reason: PropTypes.string,
    }),
    errors: PropTypes.arrayOf(
      PropTypes.shape({
        code: PropTypes.string,
        message: PropTypes.string.isRequired,
        path: PropTypes.string,
      }),
    ),
    isReady: PropTypes.bool,
    status: PropTypes.string,
  }),
  retainedAttempt: PropTypes.shape({
    missingQuotedEvidence: PropTypes.bool,
    orderId: PropTypes.string,
    recoveryMode: PropTypes.string,
    status: PropTypes.string.isRequired,
  }),
  recoveryError: PropTypes.object,
  recoveryLoading: PropTypes.bool,
  onRetryRecovery: PropTypes.func.isRequired,
  requiresPaymentReconciliation: PropTypes.bool,
  deliveryUpdateState: PropTypes.shape({
    blocked: PropTypes.bool.isRequired,
    error: PropTypes.string,
    loading: PropTypes.bool.isRequired,
  }).isRequired,
};

export default CheckoutCart;
